import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Breadcrumbs, Link, Paper,
  CircularProgress, Alert, IconButton, Tooltip, Divider, Fade, Grow,
  Skeleton, Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import api from '../../services/api.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatFetchedAt, ivrTypeMeta } from './ivrTypeMeta.js';
import IvrExtensionEditorDialog from './IvrExtensionEditorDialog.jsx';
import IvrDeleteConfirmDialog from './IvrDeleteConfirmDialog.jsx';
import IvrBackupsDialog from './IvrBackupsDialog.jsx';
import { useCkInside, Tag, Spinner, IconButton as CkIconButton } from '@NP-Webber/crm-kit/dist/next/index.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const SYSTEM_LABEL = process.env.REACT_APP_SYSTEM_LABEL || 'מערכת בחינות';

function pathSegments(path) {
  const p = String(path || '').replace(/^\/+/, '').replace(/\/+$/, '');
  if (!p) return [];
  return p.split('/').filter(Boolean);
}

function joinPath(segments) {
  return segments.join('/');
}

function ExtensionCard({ dir, detailType, detailTitle, onOpen, index }) {
  const type = detailType || dir.type || '';
  const title = detailTitle || dir.title || '';
  const { label, color, Icon } = ivrTypeMeta(type);

  return (
    <Grow in timeout={220 + Math.min(index, 8) * 40}>
      <Paper
        elevation={0}
        onClick={() => onOpen(dir)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(dir);
          }
        }}
        sx={{
          p: 2,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2.5,
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
          background: 'linear-gradient(145deg, var(--ck-surface, #ffffff) 0%, var(--ck-surface-2, #f7fbff) 100%)',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: 'var(--ck-shadow-2, 0 10px 28px rgba(13,71,161,0.12))',
            borderColor: color,
          },
          '&:focus-visible': {
            outline: `2px solid ${color}`,
            outlineOffset: 2,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: 4,
            height: '100%',
            bgcolor: color,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${color}14`,
              color,
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                color: 'var(--ck-primary, #0d47a1)',
                lineHeight: 1.1,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: 0.5,
              }}
            >
              {dir.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ mt: 0.4, minHeight: 20 }}
            >
              {title || 'ללא כותרת'}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={label}
                sx={{
                  height: 22,
                  fontSize: 11,
                  bgcolor: `${color}18`,
                  color,
                  border: 'none',
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={dir.displayPath || `/${dir.path}`}
                sx={{ height: 22, fontSize: 11, direction: 'ltr' }}
              />
            </Stack>
          </Box>
          <ChevronLeftIcon sx={{ color: 'action.disabled', mt: 0.5 }} />
        </Stack>
      </Paper>
    </Grow>
  );
}

function StatusStrip({ status, loading, onRefresh, onOpenBackups, canReadBackups }) {
  const connected = status?.connected;
  const inNextDesign = useCkInside();
  if (inNextDesign) {
    return (
      <AdminPageHeader
        title="IVR — ימות המשיח"
        sub={[
          status?.systemLabel || SYSTEM_LABEL,
          status?.session?.username || null,
          status?.fetchedAt ? `עודכן ${formatFetchedAt(status.fetchedAt)}` : null,
        ].filter(Boolean).join(' · ')}
        actions={(
          <>
            {loading && !status ? <Spinner size={18} /> : (
              <Tag tone={connected ? 'success' : 'danger'} icon={connected ? 'check' : 'alert'}>
                {connected ? 'מחובר לימות' : 'לא מחובר'}
              </Tag>
            )}
            {status?.stats?.dirs != null ? <Tag>{`${status.stats.dirs} שלוחות`}</Tag> : null}
            {status?.stats?.files != null ? <Tag>{`${status.stats.files} קבצים`}</Tag> : null}
            {canReadBackups ? <CkIconButton icon="history" label="גיבויי שלוחות שנמחקו" onClick={onOpenBackups} /> : null}
            <CkIconButton icon="refresh" label="רענון" onClick={onRefresh} disabled={loading} />
          </>
        )}
      />
    );
  }
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.18)',
        color: '#fff',
        background: 'linear-gradient(120deg, #0d47a1 0%, #1565c0 45%, #1976d2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 220,
          height: 220,
          borderRadius: '50%',
          right: -60,
          top: -80,
          background: 'radial-gradient(circle, rgba(255,255,255,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.14)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <PhoneInTalkOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: 0.3 }}>
              IVR — ימות המשיח
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {status?.systemLabel || SYSTEM_LABEL}
              {status?.session?.username ? ` · ${status.session.username}` : ''}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {loading && !status ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : (
            <Chip
              icon={connected
                ? <CheckCircleOutlineIcon sx={{ color: '#fff !important' }} />
                : <ErrorOutlineIcon sx={{ color: '#fff !important' }} />}
              label={connected ? 'מחובר לימות' : 'לא מחובר'}
              sx={{
                bgcolor: connected ? 'rgba(46,125,50,0.45)' : 'rgba(198,40,40,0.45)',
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' },
              }}
            />
          )}
          {status?.stats?.dirs != null && (
            <Chip label={`${status.stats.dirs} שלוחות`} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }} />
          )}
          {status?.stats?.files != null && (
            <Chip label={`${status.stats.files} קבצים`} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff' }} />
          )}
          {status?.fetchedAt && (
            <Typography variant="caption" sx={{ opacity: 0.75, px: 0.5 }}>
              עודכן {formatFetchedAt(status.fetchedAt)}
            </Typography>
          )}
          {canReadBackups && (
            <Tooltip title="גיבויי שלוחות שנמחקו">
              <IconButton
                onClick={onOpenBackups}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="רענון">
            <IconButton
              onClick={onRefresh}
              disabled={loading}
              sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

function DetailPanel({
  dirData,
  extension,
  loadingExt,
  currentPath,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}) {
  const fields = extension?.ini?.fields || {};
  const fieldEntries = Object.entries(fields).slice(0, 24);
  const hasExtension = Boolean(currentPath);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        minHeight: 280,
        background: 'var(--ck-surface, #fff)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <SettingsSuggestOutlinedIcon color="primary" fontSize="small" />
        <Typography fontWeight={700}>פרטי הרמה</Typography>
        <Chip
          size="small"
          label={currentPath ? `/${currentPath}` : '/'}
          sx={{ direction: 'ltr', ml: 'auto' }}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip size="small" icon={<FolderOpenIcon />} label={`${dirData?.counts?.dirs ?? 0} שלוחות`} />
        <Chip size="small" icon={<InsertDriveFileOutlinedIcon />} label={`${dirData?.counts?.files ?? 0} קבצים`} />
        <Chip size="small" label={`${dirData?.counts?.ini ?? 0} ini`} variant="outlined" />
      </Stack>

      {hasExtension && (canUpdate || canDelete) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {canUpdate && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={onEdit}
              disabled={loadingExt}
            >
              עריכת הגדרות
            </Button>
          )}
          {canDelete && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={onDelete}
              disabled={loadingExt}
            >
              מחיקה
            </Button>
          )}
        </Stack>
      )}

      {!hasExtension && (
        <Alert severity="info" sx={{ mb: 2 }}>
          זו הרמה הראשית. בחרו שלוחה מהרשימה או צרו שלוחה חדשה.
        </Alert>
      )}

      {loadingExt && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={80} />
        </Stack>
      )}

      {!loadingExt && extension?.ini && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            הגדרות שלוחה (ext.ini)
          </Typography>
          {(extension.type || extension.title) && (
            <Stack direction="row" spacing={0.75} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              {extension.type && <Chip size="small" color="primary" label={`type=${extension.type}`} />}
              {extension.title && <Chip size="small" variant="outlined" label={extension.title} />}
            </Stack>
          )}
          <Box
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              borderRadius: 2,
              bgcolor: 'var(--ck-surface-2, #f5f8fc)',
              border: '1px solid',
              borderColor: 'divider',
              p: 1.25,
            }}
          >
            {fieldEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">אין שדות ב-ini</Typography>
            ) : (
              fieldEntries.map(([k, v]) => (
                <Box
                  key={k}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr',
                    gap: 1,
                    py: 0.4,
                    borderBottom: '1px dashed var(--ck-border, rgba(0,0,0,0.06))',
                    fontSize: 13,
                  }}
                >
                  <Typography component="span" fontWeight={700} color="primary.dark" sx={{ wordBreak: 'break-all' }}>
                    {k}
                  </Typography>
                  <Typography
                    component="span"
                    color="text.secondary"
                    sx={{ wordBreak: 'break-all', direction: /https?:|ivr2:|\//i.test(v) ? 'ltr' : 'rtl', textAlign: 'start' }}
                  >
                    {v}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}

      {!loadingExt && !extension?.ini && currentPath && (
        <Alert severity="info" sx={{ mb: 2 }}>
          לא נמצא קובץ הגדרות (ext.ini) לשלוחה זו — אפשר ליצור הגדרות דרך «עריכת הגדרות».
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        קבצים ברמה זו
      </Typography>
      {(dirData?.files || []).length === 0 && (dirData?.ini || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">אין קבצים ברמה זו</Typography>
      ) : (
        <Stack spacing={0.75} sx={{ maxHeight: 240, overflow: 'auto' }}>
          {[...(dirData?.ini || []), ...(dirData?.files || [])].map((f) => (
            <Box
              key={`${f.kind}-${f.path || f.name}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'var(--ck-surface-2, #f8fafc)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{f.name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', direction: 'ltr' }}>
                  {f.displayPath || f.path}
                </Typography>
              </Box>
              <Chip size="small" label={f.kind} variant="outlined" sx={{ height: 20, fontSize: 10 }} />
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

const IvrExplorer = () => {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('ivr', 'create');
  const canUpdate = hasPermission('ivr', 'update');
  const canDelete = hasPermission('ivr', 'delete');
  const canRead = hasPermission('ivr', 'read');

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [path, setPath] = useState('');
  const [dirData, setDirData] = useState(null);
  const [dirLoading, setDirLoading] = useState(true);
  const [dirError, setDirError] = useState('');
  const [extension, setExtension] = useState(null);
  const [extLoading, setExtLoading] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [backupsOpen, setBackupsOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const segments = useMemo(() => pathSegments(path), [path]);

  const showSnack = (message, severity = 'success') => {
    setSnack({ open: true, message, severity });
  };

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get('/ivr/status');
      setStatus(data);
    } catch (err) {
      setStatus({
        connected: false,
        error: err.response?.data?.error || err.message || 'שגיאה בטעינת סטטוס',
        systemLabel: SYSTEM_LABEL,
        fetchedAt: new Date().toISOString(),
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadDir = useCallback(async (nextPath) => {
    setDirLoading(true);
    setDirError('');
    setExtension(null);
    try {
      const { data } = await api.get('/ivr/dir', { params: { path: nextPath || '' } });
      setDirData(data);
      setPath(data.path || '');
      setAnimKey((k) => k + 1);
    } catch (err) {
      setDirError(err.response?.data?.error || err.message || 'שגיאה בטעינת שלוחות');
      setDirData(null);
    } finally {
      setDirLoading(false);
    }
  }, []);

  const loadExtension = useCallback(async (extPath) => {
    if (!extPath) {
      setExtension(null);
      return;
    }
    setExtLoading(true);
    try {
      const { data } = await api.get('/ivr/extension', { params: { path: extPath } });
      setExtension(data);
    } catch {
      setExtension(null);
    } finally {
      setExtLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadDir('');
  }, [loadStatus, loadDir]);

  useEffect(() => {
    if (path) loadExtension(path);
    else setExtension(null);
  }, [path, loadExtension]);

  const handleRefresh = () => {
    loadStatus();
    loadDir(path);
  };

  const openDir = (dir) => {
    const next = dir.path || joinPath([...segments, dir.name]);
    loadDir(next);
  };

  const goToSegment = (index) => {
    if (index < 0) loadDir('');
    else loadDir(joinPath(segments.slice(0, index + 1)));
  };

  const openCreate = () => {
    setEditorMode('create');
    setEditorOpen(true);
  };

  const openEdit = () => {
    setEditorMode('edit');
    setEditorOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editorMode === 'create') {
        const body = {
          parentPath: payload.parentPath || '',
          name: payload.name,
          fields: payload.fields,
        };
        if (payload.confirmPath) body.confirmPath = payload.confirmPath;
        const { data } = await api.post('/ivr/extensions', body);
        showSnack(data.message || 'השלוחה נוצרה');
        setEditorOpen(false);
        await loadDir(path);
        if (data.extension?.path) {
          // נשארים ברמה הנוכחית — המשתמש רואה את השלוחה החדשה ברשימה
        }
      } else {
        const body = {
          path: payload.path,
          fields: payload.fields,
          mode: payload.mode || 'merge',
        };
        if (payload.confirmPath) body.confirmPath = payload.confirmPath;
        const { data } = await api.put('/ivr/extensions', body);
        showSnack(data.message || 'השלוחה עודכנה');
        setEditorOpen(false);
        await loadDir(path);
        await loadExtension(path);
      }
      loadStatus();
    } catch (err) {
      showSnack(err.response?.data?.error || err.message || 'שמירה נכשלה', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async ({ path: delPath, confirmPath, acknowledgeFiles }) => {
    setDeleting(true);
    try {
      const { data } = await api.delete('/ivr/extensions', {
        params: { path: delPath },
        data: { confirmPath, acknowledgeFiles },
      });
      showSnack(
        data.backup?.id
          ? `${data.message || 'נמחק'} · גיבוי נשמר`
          : (data.message || 'השלוחה נמחקה'),
        'success'
      );
      setDeleteOpen(false);
      // חזרה להורה
      const parts = pathSegments(delPath);
      const parent = parts.slice(0, -1).join('/');
      await loadDir(parent);
      loadStatus();
    } catch (err) {
      const code = err.response?.data?.code;
      const msg = err.response?.data?.error || err.message || 'מחיקה נכשלה';
      showSnack(msg, 'error');
      if (code === 'HAS_CHILDREN') {
        // משאירים את הדיאלוג פתוח — ההודעה ברורה
      }
    } finally {
      setDeleting(false);
    }
  };

  const childCount =
    extension?.dir?.dirs?.length ??
    dirData?.counts?.dirs ??
    0;
  const fileCount =
    ((extension?.dir?.files?.length ?? dirData?.files?.length) || 0) +
    ((extension?.dir?.ini?.length ?? dirData?.ini?.length) || 0);

  return (
    <Box>
      <StatusStrip
        status={status}
        loading={statusLoading || dirLoading}
        onRefresh={handleRefresh}
        onOpenBackups={() => setBackupsOpen(true)}
        canReadBackups={canRead}
      />

      {status && !status.connected && status.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {status.error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          px: 2,
          py: 1.25,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'var(--ck-surface, #fff)',
        }}
      >
        <Breadcrumbs
          separator={<ChevronLeftIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
          aria-label="נתיב שלוחות"
        >
          <Link
            component="button"
            underline="hover"
            color={segments.length ? 'inherit' : 'primary'}
            onClick={() => goToSegment(-1)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              fontWeight: segments.length ? 500 : 700,
              fontFamily: 'inherit',
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: 18 }} />
            ראשי
          </Link>
          {segments.map((seg, i) => (
            <Link
              key={`${seg}-${i}`}
              component="button"
              underline="hover"
              color={i === segments.length - 1 ? 'primary' : 'inherit'}
              onClick={() => goToSegment(i)}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                fontWeight: i === segments.length - 1 ? 700 : 500,
                fontFamily: 'inherit',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {seg}
            </Link>
          ))}
        </Breadcrumbs>
      </Paper>

      {dirError && (
        <Alert severity="error" sx={{ mb: 2 }}>{dirError}</Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(300px, 0.9fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap spacing={1}>
            <Typography variant="h6" fontWeight={800}>
              שלוחות
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                לחצו על שלוחה לפתיחה
              </Typography>
              {canCreate && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  disabled={!status?.connected}
                >
                  שלוחה חדשה
                </Button>
              )}
            </Stack>
          </Stack>

          {dirLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr' },
                gap: 1.5,
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={110} sx={{ borderRadius: 2.5 }} />
              ))}
            </Box>
          ) : (
            <Fade in key={animKey} timeout={280}>
              <Box>
                {(dirData?.dirs || []).length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 3,
                      bgcolor: 'var(--ck-surface, #fff)',
                    }}
                  >
                    <FolderOpenIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography fontWeight={700} gutterBottom>אין שלוחות ברמה זו</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {canCreate
                        ? 'ניתן ליצור שלוחה חדשה כאן, או לחזור לרמה הקודמת.'
                        : 'ניתן לעיין בקבצים בפאנל הפרטים, או לחזור לרמה הקודמת.'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                      {canCreate && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          שלוחה חדשה
                        </Button>
                      )}
                      {segments.length > 0 && (
                        <Button onClick={() => goToSegment(segments.length - 2)}>
                          חזרה לרמה הקודמת
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {dirData.dirs.map((d, index) => (
                      <ExtensionCard
                        key={d.path || d.name}
                        dir={d}
                        index={index}
                        onOpen={openDir}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        <DetailPanel
          dirData={dirData}
          extension={extension}
          loadingExt={extLoading}
          currentPath={path}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onEdit={openEdit}
          onDelete={() => setDeleteOpen(true)}
        />
      </Box>

      <IvrExtensionEditorDialog
        open={editorOpen}
        mode={editorMode}
        parentPath={path}
        extension={editorMode === 'edit' ? extension : null}
        saving={saving}
        onClose={() => (saving ? null : setEditorOpen(false))}
        onSave={handleSave}
      />

      <IvrDeleteConfirmDialog
        open={deleteOpen}
        path={path}
        title={extension?.title || ''}
        childCount={childCount}
        fileCount={fileCount}
        deleting={deleting}
        onClose={() => (deleting ? null : setDeleteOpen(false))}
        onConfirm={handleDelete}
      />

      <IvrBackupsDialog
        open={backupsOpen}
        canRestore={canCreate}
        onClose={() => setBackupsOpen(false)}
        onRestored={() => {
          handleRefresh();
          showSnack('הגדרות השלוחה שוחזרו מהגיבוי');
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IvrExplorer;
